import React, { useEffect, useState } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2, Loader2, Database, Layers, ListChecks } from 'lucide-react';
import { buildApiUrl } from '../../utils/apiUrl';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { pageTitleCls, pageSubtitleCls } from '../../components/ui';
import { useEsSuperUsuario } from '../../hooks/useEsSuperUsuario';
import { useAppPermisos } from '../../hooks/useAppPermisos';

type Tipo = 'cedulas' | 'bloques' | 'planmanejo';
type Msg = { tipo: 'ok' | 'err' | 'info'; texto: string } | null;

interface SyncLog {
  id: number;
  modo: string;
  iniciado_at: string;
  terminado_at: string | null;
  exito: boolean | null;
  cedulas_upsert?: number | null;
  detalles_upsert?: number | null;
  bloques_upsert?: number | null;
  duracion_ms?: number | null;
  usuario?: string | null;
  mensaje_error?: string | null;
}

export function SyncVfpPage() {
  const empresaId = useEmpresaId();
  const { esSuperUsuario, loading: cargandoSU } = useEsSuperUsuario();
  const { perm_sync_vfp, loading: cargandoPermisos } = useAppPermisos();
  const cargandoPermiso = cargandoSU || cargandoPermisos;
  const puedeUsar = esSuperUsuario || perm_sync_vfp;
  const [syncing, setSyncing] = useState<Tipo | null>(null);
  const [msg, setMsg] = useState<Msg>(null);
  const [logs, setLogs] = useState<SyncLog[]>([]);
  // Modal de confirmación (paso 1 + paso 2) para sync de cédulas (VFP congelado)
  const [confirmar, setConfirmar] = useState<{ tipo: Tipo; paso: 1 | 2 } | null>(null);

  async function cargarLogs() {
    const { data } = await supabase
      .from('app_vfp_sync_log')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('iniciado_at', { ascending: false })
      .limit(15);
    setLogs((data ?? []) as SyncLog[]);
  }

  useEffect(() => { cargarLogs(); }, [empresaId]);

  function pedirSync(tipo: Tipo) {
    if (syncing) return;
    // Para cédulas, mostrar doble confirmación modal (VFP congelado).
    if (tipo === 'cedulas') {
      setConfirmar({ tipo, paso: 1 });
      return;
    }
    // Para bloques y planmanejo, ejecutar directo.
    ejecutarSync(tipo);
  }

  async function ejecutarSync(tipo: Tipo) {
    setConfirmar(null);
    setSyncing(tipo);
    setMsg({ tipo: 'info', texto: 'Sincronizando…' });

    try {
      const path =
        tipo === 'cedulas'    ? '/aplicaciones/vfp/sincronizar' :
        tipo === 'bloques'    ? '/aplicaciones/vfp/sincronizar-bloques' :
                                '/aplicaciones/vfp/sincronizar-planmanejo';

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setMsg({ tipo: 'err', texto: 'Sesion expirada. Vuelva a iniciar sesion antes de sincronizar.' });
        return;
      }

      const resp = await fetch(buildApiUrl(path), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Empresa-Id': String(empresaId),
        },
        body: JSON.stringify({ modo: 'incremental', usuario: 'ui', empresa_id: empresaId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.exito === false) {
        const m = data?.mensaje_error || `Error HTTP ${resp.status}`;
        setMsg({ tipo: 'err', texto: `Falló: ${m}` });
      } else {
        const seg = Math.round((data.duracion_ms || 0) / 1000);
        const partes: string[] = [];
        if (data.cedulas_upsert != null) partes.push(`${data.cedulas_upsert} cédulas`);
        if (data.detalles_upsert != null) partes.push(`${data.detalles_upsert} detalles`);
        if (data.bloques_upsert != null) partes.push(`${data.bloques_upsert} bloques`);
        if (data.boletas_creadas != null) partes.push(`${data.boletas_creadas} boletas ERP`);
        if (data.productos_insertados != null) partes.push(`${data.productos_insertados} productos`);
        if (data.secuencia_actualizada != null) partes.push(`seq ${data.secuencia_actualizada}`);
        if (data.lotes_sin_match) partes.push(`${data.lotes_sin_match} sin match`);
        setMsg({ tipo: 'ok', texto: `${partes.join(' · ')} · ${seg}s` });
      }
      cargarLogs();
    } catch (e: any) {
      setMsg({
        tipo: 'err',
        texto: `Sin conexión con backend local. ¿Está corriendo? (${e?.message || 'fetch error'})`,
      });
    } finally {
      setSyncing(null);
      setTimeout(() => setMsg(null), 15000);
    }
  }

  function fmtDate(s: string | null): string {
    if (!s) return '—';
    return new Date(s).toLocaleString('es-CR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  }

  if (cargandoPermiso) return null;

  if (!puedeUsar) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <h1 className={pageTitleCls}>Sync VFP</h1>
        <p className={pageSubtitleCls}>No tenés el permiso "Sincronizar VFP" — puede sobrescribir datos transaccionales del ERP. Pedile a un administrador que te lo asigne en Configuración → Roles.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-4">
        <h1 className={pageTitleCls}>Sync VFP</h1>
        <p className={pageSubtitleCls}>Sincronización manual desde DBFs de VFP hacia el ERP (cédulas operativas y catálogos).</p>
      </div>

      {/* Warning VFP congelado */}
      <div className="rounded-lg p-4 mb-5 flex gap-3"
        style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
        <AlertTriangle size={18} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 2 }} />
        <div className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>
          <strong>VFP está congelado.</strong> El ERP es el sistema transaccional desde la semana 23-26.
          Usá estos botones solo en casos excepcionales (modificación a catálogos en VFP, rescate de datos).
          Los syncs requieren que esta PC tenga la unidad <code>Z:</code> mapeada al servidor VFP.
        </div>
      </div>

      {/* Botones de sync */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Cédulas */}
        <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Database size={16} style={{ color: 'var(--tone-info-text)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Cédulas VFP</span>
          </div>
          <p className="text-xs mb-3" style={{ color: 'var(--ink-faint)' }}>
            Importa cédulas nuevas de <code>conseboleta.dbf</code> + <code>detalleaplica.dbf</code> al ERP.
            Requiere doble confirmación.
          </p>
          <button
            onClick={() => pedirSync('cedulas')}
            disabled={syncing !== null}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-xs font-semibold"
            style={{
              background: 'var(--tone-info-bg)',
              border: '1px solid var(--tone-info-border)',
              color: 'var(--tone-info-text)',
              opacity: syncing === 'cedulas' ? 0.6 : 1,
              cursor: syncing ? 'wait' : 'pointer',
            }}>
            {syncing === 'cedulas' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            {syncing === 'cedulas' ? 'Sincronizando…' : 'Cédulas VFP'}
          </button>
        </div>

        {/* Bloques */}
        <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Layers size={16} style={{ color: 'var(--emp-accent-txt)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Bloques VFP</span>
          </div>
          <p className="text-xs mb-3" style={{ color: 'var(--ink-faint)' }}>
            Sincroniza <code>bloques.dbf</code> → <code>emp_bloques</code>:
            estadios, áreas, fechas ancla (1er y 2do ciclo), grupos forza.
          </p>
          <button
            onClick={() => pedirSync('bloques')}
            disabled={syncing !== null}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-xs font-semibold"
            style={{
              background: 'var(--emp-accent-bg)',
              border: '1px solid var(--emp-accent)',
              color: 'var(--emp-accent-txt)',
              opacity: syncing === 'bloques' ? 0.6 : 1,
              cursor: syncing ? 'wait' : 'pointer',
            }}>
            {syncing === 'bloques' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            {syncing === 'bloques' ? 'Sincronizando…' : 'Bloques VFP'}
          </button>
        </div>

        {/* Plan de Manejo */}
        <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
          <div className="flex items-center gap-2 mb-2">
            <ListChecks size={16} style={{ color: 'var(--tone-success-text)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Plan de Manejo</span>
          </div>
          <p className="text-xs mb-3" style={{ color: 'var(--ink-faint)' }}>
            Sincroniza <code>planmanejo.dbf</code> + <code>detalleciclos.dbf</code> →
            <code>app_plan_ciclos</code> + <code>app_plan_productos</code>. Borra y recarga.
          </p>
          <button
            onClick={() => pedirSync('planmanejo')}
            disabled={syncing !== null}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-xs font-semibold"
            style={{
              background: 'var(--tone-success-bg)',
              border: '1px solid var(--tone-success-border)',
              color: 'var(--tone-success-text)',
              opacity: syncing === 'planmanejo' ? 0.6 : 1,
              cursor: syncing ? 'wait' : 'pointer',
            }}>
            {syncing === 'planmanejo' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            {syncing === 'planmanejo' ? 'Sincronizando…' : 'Plan de Manejo'}
          </button>
        </div>
      </div>

      {/* Mensaje resultado */}
      {msg && (
        <div className="rounded-lg p-3 mb-4 flex items-center gap-2"
          style={{
            background:
              msg.tipo === 'ok'  ? 'var(--tone-success-bg)' :
              msg.tipo === 'err' ? 'var(--tone-warn-bg)' :
                                   'var(--surface-deep)',
            border: `1px solid ${
              msg.tipo === 'ok'  ? 'var(--tone-success-border)' :
              msg.tipo === 'err' ? 'var(--tone-warn-border)' :
                                   'var(--line)'}`,
            color:
              msg.tipo === 'ok'  ? 'var(--tone-success-text)' :
              msg.tipo === 'err' ? 'var(--tone-warn-text)' :
                                   'var(--ink-muted)',
          }}>
          {msg.tipo === 'ok' ? <CheckCircle2 size={14} /> :
           msg.tipo === 'err' ? <AlertTriangle size={14} /> :
                                <Loader2 size={14} className="animate-spin" />}
          <span className="text-xs">{msg.texto}</span>
        </div>
      )}

      {/* Histórico de syncs */}
      <div className="rounded-lg overflow-hidden" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
        <div className="px-4 py-2.5 flex items-center justify-between"
          style={{ borderBottom: '1px solid var(--line)' }}>
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>
            Histórico de sincronizaciones
          </span>
          <button onClick={cargarLogs}
            className="text-xs flex items-center gap-1 px-2 py-1 rounded"
            style={{ color: 'var(--ink-faint)' }}>
            <RefreshCw size={11} /> Actualizar
          </button>
        </div>
        {logs.length === 0 ? (
          <p className="text-xs text-center py-6" style={{ color: 'var(--ink-faint)' }}>
            Sin sincronizaciones registradas.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line-dim)' }}>
                {['Fecha', 'Modo', 'Estado', 'Resultado', 'Duración', 'Usuario'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: 'var(--ink-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={l.id}
                  style={{
                    borderBottom: '1px solid var(--line-dim)',
                    background: i % 2 === 0 ? 'transparent' : 'var(--surface-deep)',
                  }}>
                  <td className="px-3 py-1.5 font-mono" style={{ color: 'var(--ink)' }}>{fmtDate(l.iniciado_at)}</td>
                  <td className="px-3 py-1.5 font-mono" style={{ color: 'var(--ink-muted)' }}>{l.modo}</td>
                  <td className="px-3 py-1.5">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                      style={l.exito
                        ? { background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }
                        : { background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)' }}>
                      {l.exito ? 'OK' : 'ERROR'}
                    </span>
                  </td>
                  <td className="px-3 py-1.5" style={{ color: 'var(--ink-muted)' }}>
                    {[
                      l.cedulas_upsert != null ? `${l.cedulas_upsert} ced` : null,
                      l.detalles_upsert != null ? `${l.detalles_upsert} det` : null,
                      l.bloques_upsert != null ? `${l.bloques_upsert} blq` : null,
                    ].filter(Boolean).join(' · ') || (l.mensaje_error ?? '—')}
                  </td>
                  <td className="px-3 py-1.5 font-mono" style={{ color: 'var(--ink-muted)' }}>
                    {l.duracion_ms ? `${Math.round(l.duracion_ms / 1000)}s` : '—'}
                  </td>
                  <td className="px-3 py-1.5" style={{ color: 'var(--ink-muted)' }}>{l.usuario || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de confirmación para sync de cédulas (VFP congelado, doble paso) */}
      {confirmar && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div style={{
            width: '100%', maxWidth: '480px', borderRadius: '18px', padding: '24px',
            background: '#1a1206', border: '1px solid rgba(245,158,11,0.4)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#fbbf24', marginBottom: '10px' }}>
              {confirmar.paso === 1 ? '⚠ VFP está congelado' : '⚠ Confirmación final'}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--ink-muted)', lineHeight: 1.55, marginBottom: '20px' }}>
              {confirmar.paso === 1 ? (
                <>
                  Última cédula sincronizada: <strong style={{ color: '#fde68a' }}>4581</strong> (sem 22-26).
                  <br /><br />
                  El ERP es ahora el sistema transaccional desde la sem 23-26.
                  <br /><br />
                  ¿Realmente querés reabrir el sync? Solo si VFP volvió a generar cédulas por error.
                </>
              ) : (
                <>
                  ¿Estás <strong style={{ color: '#fde68a' }}>100% seguro</strong> de sincronizar VFP?
                  <br /><br />
                  Esto puede traer cédulas duplicadas con las del ERP.
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setConfirmar(null)} style={{
                flex: 1, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
                border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
              }}>Cancelar</button>
              <button
                onClick={() => {
                  if (confirmar.paso === 1) setConfirmar({ tipo: confirmar.tipo, paso: 2 });
                  else ejecutarSync(confirmar.tipo);
                }}
                style={{
                  flex: 2, padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 800,
                  border: 'none', background: 'linear-gradient(135deg,#f59e0b,#fbbf24)',
                  color: '#1f1300', cursor: 'pointer',
                }}>
                {confirmar.paso === 1 ? 'Continuar' : 'Sincronizar ahora'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
